import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  PlusCircle,
  AlertTriangle,
  FileText,
  Search,
  X,
  Calendar,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import LclBoardPageTransition from '../components/operations/LclBoardPageTransition';
import LclRequestDetailDialog from '../components/LclRequestDetailDialog';
import LclTraceabilitySection from '../components/LclTraceabilitySection';
import LclProviderInvitationModal from '../components/LclProviderInvitationModal';
import CargarLclCotizacionDialog from '../cotizaciones/components/CargarLclCotizacionDialog.jsx';
import ComparadorCotizacionesDialog from '../cotizaciones/components/ComparadorCotizacionesDialog.jsx';
import LclOfertasSection from '../ofertas/components/LclOfertasSection.jsx';
import LclInstruccionesEmbarqueDialog from '../instrucciones/components/LclInstruccionesEmbarqueDialog.jsx';
import NotasOperacionDialog from '../operaciones/components/NotasOperacionDialog.jsx';
import RecargosOperacionDialog from '../operaciones/components/RecargosOperacionDialog.jsx';
import { fetchLclRequests, deleteLclRequest } from '../Services/lclRequestsService';
import {
  shortId,
  formatCodigo,
  formatCliente,
  formatRuta,
  formatDate,
} from '../utils/lclFormatters';
import {
  ITEMS_PER_PAGE,
  VENCIDA_DIAS,
  SUBESTADO_BADGE,
  SUBESTADO_METRIC_COLOR,
} from '../utils/lclBoardConstants';

/**
 * Tablero base de los 4 boards del módulo LCL.
 *
 * Los 4 tableros del aéreo son ~2.600 líneas casi idénticas repetidas
 * en 4 archivos. Aquí la parte común vive una sola vez y cada tablero
 * (`LclQuotationsBoardPage`, `LclInProgressBoardPage`, …) solo aporta
 * su configuración. Sigue siendo código 100% del LCL: no se comparte
 * nada con el aéreo.
 *
 * Props de configuración:
 *  - estado:            fase principal que filtra el tablero
 *  - titulo, subtitulo: encabezado
 *  - icon, iconClass:   icono del encabezado
 *  - subEstadoOptions:  [{value,label}] de las métricas y del filtro
 *                       (el sub-estado es informativo: ya no se edita
 *                       desde el tablero)
 *  - subEstadoDefault:  sub-estado que se asume cuando la fila no tiene
 *  - computeSubEstado:  (row) => string — opcional, para sub-estados
 *                       derivados (ej. "Vencida" por antigüedad)
 *  - mostrarAntiguedad: muestra la columna de días desde la última
 *                       actualización, resaltada al pasar el umbral
 *  - exportFileName:    prefijo del archivo Excel
 *  - permiteInvitar:    habilita "Solicitar cotización" (solo tiene
 *                       sentido en el tablero de Cotizaciones)
 */
const daysSince = (v) => {
  if (!v) return 0;
  const d = new Date(v);
  if (isNaN(d.getTime())) return 0;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
};

export default function LclBoardPage({
  estado,
  titulo,
  subtitulo,
  icon: Icon = FileText,
  iconClass = 'bg-amber-100 text-amber-700',
  subEstadoOptions = [],
  subEstadoDefault = '',
  computeSubEstado,
  mostrarAntiguedad = false,
  exportFileName = 'lcl',
  permiteInvitar = false,
  permiteCargarCotizacion = false,
  permiteInstrucciones = false,
  permiteSeguimiento = false,
}) {
  const toast = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterSubEstado, setFilterSubEstado] = useState('');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);

  // Acciones
  const [detailRequest, setDetailRequest] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [traceabilityRequest, setTraceabilityRequest] = useState(null);
  const [invitationRequest, setInvitationRequest] = useState(null);
  const [cotizacionRequest, setCotizacionRequest] = useState(null);
  const [comparadorRequest, setComparadorRequest] = useState(null);
  const [ofertaRequest, setOfertaRequest] = useState(null);
  const [instruccionesRequest, setInstruccionesRequest] = useState(null);
  const [notasRequest, setNotasRequest] = useState(null);
  const [recargosRequest, setRecargosRequest] = useState(null);

  // Sub-estado efectivo de una fila: el derivado si el tablero define
  // uno (ej. "Vencida"), si no el guardado, si no el default.
  const resolveSubEstado = useCallback(
    (r) => {
      if (computeSubEstado) return computeSubEstado(r);
      return r?.subEstado || subEstadoDefault;
    },
    [computeSubEstado, subEstadoDefault],
  );

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setLoading(true);
    try {
      const query = { estado };
      if (filterFechaDesde || filterFechaHasta) {
        query.createdAt = {};
        if (filterFechaDesde) query.createdAt.$gte = new Date(filterFechaDesde);
        if (filterFechaHasta) query.createdAt.$lte = new Date(filterFechaHasta);
      }
      const r = await fetchLclRequests({
        limit: 200, // traemos todo y filtramos/paginamos en cliente
        offset: 1,
        query,
        sort: { updatedAt: -1 },
      });
      setRows(r?.data || []);
    } catch (e) {
      if (!silencioso) toast.error(`Error al cargar ${titulo.toLowerCase()}`, e?.message);
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [estado, filterFechaDesde, filterFechaHasta, titulo, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Filtros en cliente
  const filtered = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    return rows
      .map((r) => ({ ...r, subEstado: resolveSubEstado(r) }))
      .filter((r) => {
        if (filterSubEstado && r.subEstado !== filterSubEstado) return false;
        if (!q) return true;
        return [
          r.codigo,
          r.clienteSnapshot?.nombre,
          r.clienteSnapshot?.nit,
          r.puertoCargaSnapshot?.unlocode,
          r.puertoCargaSnapshot?.ciudad,
          r.puertoDescargaSnapshot?.unlocode,
          r.puertoDescargaSnapshot?.ciudad,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
  }, [rows, debouncedSearch, filterSubEstado, resolveSubEstado]);

  // Resetear a página 1 si cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterSubEstado, filterFechaDesde, filterFechaHasta]);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  const filtrosActivos = Boolean(
    search || filterSubEstado || filterFechaDesde || filterFechaHasta,
  );

  const limpiarFiltros = () => {
    setSearch('');
    setFilterSubEstado('');
    setFilterFechaDesde('');
    setFilterFechaHasta('');
  };

  // ---- Acciones ----
  const handleTableAction = useCallback((action, row) => {
    switch (action) {
      case 'view':
        setDetailRequest(row);
        break;
      case 'traceability':
        setTraceabilityRequest(row);
        break;
      case 'invite':
        setInvitationRequest(row);
        break;
      case 'uploadQuote':
        setCotizacionRequest(row);
        break;
      case 'compareQuotes':
        setComparadorRequest(row);
        break;
      case 'createOffer':
        setOfertaRequest(row);
        break;
      case 'shippingInstructions':
        setInstruccionesRequest(row);
        break;
      case 'operationNotes':
        setNotasRequest(row);
        break;
      case 'operationSurcharges':
        setRecargosRequest(row);
        break;
      case 'delete':
        setDeleteTarget(row);
        break;
      default:
        break;
    }
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const label = formatCodigo(deleteTarget);
    const r = await deleteLclRequest(deleteTarget._id);
    if (r?.success) {
      toast.success('Solicitud eliminada', label);
      load();
    } else {
      toast.error('No se pudo eliminar', r?.message);
    }
    setDeleteTarget(null);
  };

  // ---- Métricas por sub-estado ----
  const metrics = useMemo(() => {
    const out = {};
    for (const opt of subEstadoOptions) out[opt.value] = 0;
    for (const r of rows) {
      const se = resolveSubEstado(r);
      if (se in out) out[se] += 1;
    }
    return out;
  }, [rows, subEstadoOptions, resolveSubEstado]);

  // ---- Columnas de la tabla ----
  const tableColumns = useMemo(() => {
    const cols = [
      {
        id: 'codigo',
        label: 'Código',
        sortable: true,
        render: (row) => (
          <span className="font-mono text-xs font-semibold">{formatCodigo(row)}</span>
        ),
      },
      {
        id: 'cliente',
        label: 'Cliente',
        sortable: true,
        render: (row) => (
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" title={formatCliente(row)}>
              {formatCliente(row)}
            </div>
            {row.clienteSnapshot?.nit && (
              <div className="text-[10px] text-muted-foreground font-mono">
                NIT {row.clienteSnapshot.nit}
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'ruta',
        label: 'Ruta',
        render: (row) => (
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground truncate" title={formatRuta(row)}>
              {formatRuta(row)}
            </div>
            {row.tipoServicio && (
              <div className="text-[10px] text-muted-foreground/80">{row.tipoServicio}</div>
            )}
          </div>
        ),
      },
      {
        id: 'carga',
        label: 'Carga',
        render: (row) => (
          <div className="text-xs text-muted-foreground tabular-nums">
            <div>
              {row.numPiezas ?? 0} bultos · {Number(row.pesoTotal || 0).toFixed(2)} kg
            </div>
            <div className="font-mono">
              {Number(row.volumenTotal || 0).toFixed(3)} m³ ·{' '}
              <span className="font-semibold text-foreground">
                {Number(row.unidadesWM || 0).toFixed(3)} W/M
              </span>
            </div>
          </div>
        ),
      },
      {
        id: 'subEstado',
        label: 'Sub-estado',
        sortable: true,
        render: (row) => {
          const se = resolveSubEstado(row);
          if (!se) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <Badge
              variant="outline"
              className={cn('font-normal text-[10px] py-0 px-1.5', SUBESTADO_BADGE[se])}
            >
              {se}
            </Badge>
          );
        },
      },
    ];

    if (mostrarAntiguedad) {
      cols.push({
        id: 'antiguedad',
        label: 'Antigüedad',
        sortable: true,
        render: (row) => {
          const d = daysSince(row.updatedAt || row.createdAt);
          const isOld = d > VENCIDA_DIAS;
          return (
            <span
              className={cn(
                'text-xs tabular-nums font-mono',
                isOld ? 'text-rose-600 font-semibold' : 'text-muted-foreground',
              )}
            >
              {d}d
            </span>
          );
        },
      });
    }

    cols.push(
      {
        id: 'updatedAt',
        label: 'Actualizado',
        sortable: true,
        render: (row) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.updatedAt || row.createdAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        label: 'Acciones',
        actions: [
          { key: 'view', icon: 'Eye', tooltip: 'Ver detalle' },
          { key: 'traceability', icon: 'Hash', tooltip: 'Trazabilidad' },
          // Solo en Cotizaciones: pedirle tarifa a los proveedores.
          ...(permiteInvitar
            ? [{ key: 'invite', icon: 'Send', tooltip: 'Solicitar cotización' }]
            : []),
          ...(permiteCargarCotizacion
            ? [
                {
                  key: 'uploadQuote',
                  icon: 'Upload',
                  tooltip: 'Cargar cotización de proveedor',
                },
                {
                  key: 'compareQuotes',
                  icon: 'Scale',
                  tooltip: 'Comparar cotizaciones',
                },
                {
                  key: 'createOffer',
                  icon: 'FileText',
                  tooltip: 'Oferta al cliente',
                },
                {
                  key: 'shippingInstructions',
                  icon: 'PlayCircle',
                  tooltip: 'Iniciar operación (instrucciones de embarque)',
                },
              ]
            : []),
          // En Operaciones en curso: corregirlas o reenviarlas al proveedor.
          ...(permiteInstrucciones
            ? [
                {
                  key: 'shippingInstructions',
                  icon: 'ClipboardList',
                  tooltip: 'Instrucciones de embarque',
                },
              ]
            : []),
          // Seguimiento de la operación en curso.
          ...(permiteSeguimiento
            ? [
                { key: 'operationNotes', icon: 'StickyNote', tooltip: 'Notas de la operación' },
                {
                  key: 'operationSurcharges',
                  icon: 'Receipt',
                  tooltip: 'Recargos de la operación',
                },
              ]
            : []),
          // Una operación en curso no se elimina (el backend también lo
          // impide): si no sigue, se cancela.
          ...(estado === 'Enviada'
            ? []
            : [{ key: 'delete', icon: 'Trash', tooltip: 'Eliminar solicitud' }]),
        ],
      },
    );

    return cols;
  }, [
    mostrarAntiguedad,
    resolveSubEstado,
    permiteInvitar,
    permiteCargarCotizacion,
    permiteInstrucciones,
    permiteSeguimiento,
    estado,
  ]);

  // ---- Columnas para Excel ----
  const excelColumns = useMemo(
    () => [
      { key: 'codigo', header: 'Código' },
      { key: 'clienteSnapshot.nombre', header: 'Cliente' },
      { key: 'clienteSnapshot.nit', header: 'NIT' },
      { key: 'tipoServicio', header: 'Servicio' },
      { key: 'puertoCargaSnapshot.unlocode', header: 'Puerto origen' },
      { key: 'puertoDescargaSnapshot.unlocode', header: 'Puerto destino' },
      { key: 'numPiezas', header: 'Bultos' },
      { key: 'pesoTotal', header: 'Peso (kg)' },
      { key: 'volumenTotal', header: 'Volumen (m³)' },
      { key: 'unidadesWM', header: 'Unidades W/M' },
      { key: 'incotermSnapshot.codigo', header: 'Incoterm' },
      { key: 'estado', header: 'Estado' },
      { key: 'subEstado', header: 'Sub-estado' },
      { key: 'createdAt', header: 'Creada' },
      { key: 'updatedAt', header: 'Actualizada' },
    ],
    [],
  );

  // Export: trae todas las filas del estado, no solo la página.
  const exportService = useCallback(
    async ({ query = {}, sort } = {}) =>
      fetchLclRequests({
        limit: 10000,
        offset: 1,
        query: { ...query, estado },
        sort: sort || { createdAt: -1 },
      }),
    [estado],
  );

  return (
    <LclBoardPageTransition>
      <div className="space-y-4">
        {/* Header */}
        <Card>
          <CardContent className="p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/lcl')}
                  aria-label="Volver al tablero LCL"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className={cn('p-2 rounded-lg', iconClass)}>
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-foreground">{titulo}</h1>
                  <p className="text-xs text-muted-foreground">{subtitulo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ExportExcelButton
                  service={exportService}
                  columns={excelColumns}
                  fileName={`${exportFileName}_${new Date().toISOString().slice(0, 10)}`}
                  filters={{ estado }}
                />
                <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate('/lcl')}>
                  <PlusCircle className="h-4 w-4" />
                  Nueva
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Métricas de sub-estado */}
        {subEstadoOptions.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {subEstadoOptions.map((opt) => {
              const MetricIcon = opt.value === 'Vencida' ? AlertTriangle : FileText;
              const count = metrics[opt.value] || 0;
              const active = filterSubEstado === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setFilterSubEstado(active ? '' : opt.value)}
                  className={cn(
                    'text-left rounded-lg border-2 bg-card p-3 hover:shadow-md transition-shadow',
                    active ? 'border-primary' : 'border-border',
                    SUBESTADO_METRIC_COLOR[opt.value],
                  )}
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-semibold opacity-80">
                    <MetricIcon className="h-3 w-3" />
                    {opt.label}
                  </div>
                  <div className="text-2xl font-bold mt-1 tabular-nums">{count}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Filtros inline */}
        <Card>
          <CardContent className="p-3">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-5 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Buscar por código, cliente, NIT o puerto…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="md:col-span-3">
                <select
                  value={filterSubEstado}
                  onChange={(e) => setFilterSubEstado(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Todos los sub-estados</option>
                  {subEstadoOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="date"
                  value={filterFechaDesde}
                  onChange={(e) => setFilterFechaDesde(e.target.value)}
                  className="pl-9"
                  title="Fecha desde"
                />
              </div>
              <div className="md:col-span-1 relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="date"
                  value={filterFechaHasta}
                  onChange={(e) => setFilterFechaHasta(e.target.value)}
                  className="pl-9"
                  title="Fecha hasta"
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={limpiarFiltros}
                  disabled={!filtrosActivos}
                  className="w-full"
                  title="Limpiar filtros"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <AppliedFilters
              filters={{
                ...(filterSubEstado && { subEstado: filterSubEstado }),
                ...(filterFechaDesde && { fechaDesde: filterFechaDesde }),
                ...(filterFechaHasta && { fechaHasta: filterFechaHasta }),
              }}
              fields={[
                { id: 'subEstado', label: 'Sub-estado', options: subEstadoOptions },
                { id: 'fechaDesde', label: 'Desde' },
                { id: 'fechaHasta', label: 'Hasta' },
              ]}
              onRemoveFilter={(k) => {
                if (k === 'subEstado') setFilterSubEstado('');
                if (k === 'fechaDesde') setFilterFechaDesde('');
                if (k === 'fechaHasta') setFilterFechaHasta('');
              }}
              onClear={limpiarFiltros}
            />
          </CardContent>
        </Card>

        {/* Tabla */}
        <DataTable
          data={pageItems}
          columns={tableColumns}
          isLoading={loading}
          onAction={handleTableAction}
          emptyMessage={`Sin solicitudes en ${titulo.toLowerCase()}`}
          emptyHint={
            rows.length === 0
              ? `No hay solicitudes LCL en estado ${estado}.`
              : 'Ninguna solicitud coincide con los filtros aplicados.'
          }
        />

        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        )}

        {/* Confirm: eliminar */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar solicitud LCL?</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminará <strong>{deleteTarget ? formatCodigo(deleteTarget) : ''}</strong>.
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <LclProviderInvitationModal
          open={!!invitationRequest}
          onOpenChange={(o) => !o && setInvitationRequest(null)}
          lclRequest={invitationRequest}
          onSent={() => load()}
        />

        {ofertaRequest && (
          <Dialog open onOpenChange={(o) => !o && setOfertaRequest(null)}>
            <DialogContent className="w-[92vw] max-w-none max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Oferta al cliente · {ofertaRequest.codigo}</DialogTitle>
              </DialogHeader>
              <LclOfertasSection
                lclRequestId={ofertaRequest._id}
                lclRequest={ofertaRequest}
                onChanged={() => load()}
              />
            </DialogContent>
          </Dialog>
        )}

        {comparadorRequest && (
          <ComparadorCotizacionesDialog
            open
            onOpenChange={(o) => !o && setComparadorRequest(null)}
            lclRequest={comparadorRequest}
            onChanged={() => load()}
          />
        )}

        {cotizacionRequest && (
          <CargarLclCotizacionDialog
            open
            onOpenChange={(o) => !o && setCotizacionRequest(null)}
            lclRequest={cotizacionRequest}
            onSaved={() => load()}
          />
        )}

        <LclRequestDetailDialog
          request={detailRequest}
          open={!!detailRequest}
          onOpenChange={(o) => !o && setDetailRequest(null)}
          onChanged={load}
          onAbrirInstrucciones={(r) => {
            // Se cierra el detalle: si la operación arranca, el estado que
            // muestra quedaría viejo.
            setDetailRequest(null);
            setInstruccionesRequest(r);
          }}
        />

        {notasRequest && (
          <NotasOperacionDialog
            open
            lclRequest={notasRequest}
            onOpenChange={(o) => !o && setNotasRequest(null)}
          />
        )}

        {recargosRequest && (
          <RecargosOperacionDialog
            open
            lclRequest={recargosRequest}
            onOpenChange={(o) => !o && setRecargosRequest(null)}
          />
        )}

        {instruccionesRequest && (
          <LclInstruccionesEmbarqueDialog
            open
            lclRequestId={instruccionesRequest._id}
            onOpenChange={(o) => !o && setInstruccionesRequest(null)}
            // Sin spinner: el modal sigue abierto encima del tablero.
            onChanged={() => load({ silencioso: true })}
          />
        )}

        {/* Dialog: Trazabilidad */}
        <Dialog
          open={!!traceabilityRequest}
          onOpenChange={(o) => !o && setTraceabilityRequest(null)}
        >
          <DialogContent className="w-[80vw] max-w-none max-h-[90vh] p-0 overflow-hidden flex flex-col">
            <DialogHeader className="px-6 py-4 border-b bg-muted/30 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="space-y-0.5">
                  <DialogTitle className="text-lg">
                    Trazabilidad ·{' '}
                    {traceabilityRequest?.codigo || shortId(traceabilityRequest?._id)}
                  </DialogTitle>
                  <DialogDescription className="text-xs">
                    Cliente: {formatCliente(traceabilityRequest || {})} ·{' '}
                    {formatRuta(traceabilityRequest || {})}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
              {traceabilityRequest ? (
                <LclTraceabilitySection
                  lclRequestId={traceabilityRequest._id}
                  clienteSnapshot={traceabilityRequest.clienteSnapshot}
                />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </LclBoardPageTransition>
  );
}
